import { useEffect, useRef, useState } from "react";
import client from "@/lib/api";

/**
 * Load the Google Maps SDK (+ Places) using a settings-managed API key, and
 * initialise a map + draggable marker + Places Autocomplete inside the given DOM refs.
 *
 * @param {{
 *   open: boolean,                     // dialog open flag
 *   branch: any,                       // used for initial center + fallback lat/lng
 *   mapElRef: React.MutableRefObject,  // ref to the map container <div>
 *   searchInputRef: React.MutableRefObject, // ref to the Places-bound <input>
 *   onMarkerMoved: ({lat,lng}) => void,
 * }} args
 * @returns {{ apiKey: string|null, loading: boolean, mapReady: boolean,
 *             mapError: 'auth'|'network'|null, mapRef: MutableRef,
 *             markerRef: MutableRef, applyLatLng: (lat,lng) => void }}
 */
export function useGoogleMaps({ open, branch, mapElRef, searchInputRef, onMarkerMoved }) {
  const [apiKey, setApiKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(null);

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Fetch the API key + reset state on open.
  useEffect(() => {
    if (!open) return;
    setMapReady(false);
    setMapError(null);
    setLoading(true);
    client.get(`/settings/google_maps_api_key`).then(({ data }) => {
      setApiKey(data?.value || null);
    }).catch((err) => {
      console.debug("Could not read google_maps_api_key:", err);
      setApiKey(null);
    }).finally(() => setLoading(false));
  }, [open]);

  // Register the SDK's auth-failure hook.
  useEffect(() => {
    window.gm_authFailure = () => {
      setMapError("auth");
      setMapReady(false);
    };
    return () => {
      try {
        delete window.gm_authFailure;
      } catch (err) {
        console.debug("gm_authFailure cleanup:", err);
        window.gm_authFailure = undefined;
      }
    };
  }, []);

  const initMap = () => {
    if (!window.google?.maps || !mapElRef.current) {
      setTimeout(initMap, 100);
      return;
    }
    const center = {
      lat: branch?.latitude ?? 51.1657,
      lng: branch?.longitude ?? 10.4515,
    };
    mapRef.current = new window.google.maps.Map(mapElRef.current, {
      center,
      zoom: branch?.latitude ? 16 : 6,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: true,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.TOP_RIGHT,
        mapTypeIds: ["roadmap", "satellite"],
      },
    });
    markerRef.current = new window.google.maps.Marker({
      position: center,
      map: mapRef.current,
      draggable: true,
    });
    markerRef.current.addListener("dragend", (e) => {
      onMarkerMoved({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });
    mapRef.current.addListener("click", (e) => {
      markerRef.current.setPosition(e.latLng);
      onMarkerMoved({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    });

    if (searchInputRef.current && window.google.maps.places) {
      autocompleteRef.current = new window.google.maps.places.Autocomplete(
        searchInputRef.current,
        { fields: ["geometry", "formatted_address", "name"] },
      );
      autocompleteRef.current.bindTo("bounds", mapRef.current);
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current.getPlace();
        if (!place?.geometry?.location) return;
        const p = place.geometry.location;
        onMarkerMoved({ lat: p.lat(), lng: p.lng() });
        mapRef.current.setCenter(p);
        mapRef.current.setZoom(17);
        markerRef.current.setPosition(p);
      });
    }
    setMapReady(true);
  };

  // Load / re-use the SDK <script>.
  useEffect(() => {
    if (!open || !apiKey || mapError) return;
    const wantSrc = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=weekly`;
    let script = document.getElementById("gmaps-sdk");
    if (script && script.src !== wantSrc) {
      script.remove();
      script = null;
      try {
        delete window.google;
      } catch (err) {
        console.debug("delete window.google failed:", err);
      }
    }
    if (script) {
      if (window.google?.maps) initMap();
      else script.addEventListener("load", initMap, { once: true });
      return;
    }
    script = document.createElement("script");
    script.id = "gmaps-sdk";
    script.src = wantSrc;
    script.async = true;
    script.defer = true;
    script.onload = initMap;
    script.onerror = () => setMapError("network");
    document.head.appendChild(script);
  }, [open, apiKey, mapError]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyLatLng = (lat, lng) => {
    if (mapRef.current && markerRef.current && window.google?.maps) {
      const p = new window.google.maps.LatLng(lat, lng);
      mapRef.current.setCenter(p);
      mapRef.current.setZoom(17);
      markerRef.current.setPosition(p);
    }
  };

  return { apiKey, loading, mapReady, mapError, applyLatLng };
}
